import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface Post {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  audio_url?: string;
  audio_title?: string;
  audio_duration?: string;
  image_url?: string;
  likes_count: number;
  comments_count: number;
  profiles?: {
    username: string;
    artist_types: string[];
    avatar_url?: string;
  };
}

export async function createPost(content: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  const { data: post, error } = await supabase
    .from('posts')
    .insert([{ 
      content, 
      user_id: user.data.user.id 
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating post:', error);
    throw error;
  }

  // Fetch the profile data separately
  const { data: profile } = await supabase
    .from('profiles')
    .select('username, artist_types')
    .eq('id', post.user_id)
    .single();

  return { ...post, profiles: profile };
}

export async function deletePost(postId: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  // First verify that the user owns this post
  const { data: post, error: fetchError } = await supabase
    .from('posts')
    .select('user_id')
    .eq('id', postId)
    .single();

  if (fetchError) {
    console.error('Error fetching post:', fetchError);
    throw fetchError;
  }

  if (!post) {
    throw new Error('Post not found');
  }

  if (post.user_id !== user.data.user.id) {
    throw new Error('Not authorized to delete this post');
  }

  // Then delete the post
  const { error: deleteError } = await supabase
    .from('posts')
    .delete()
    .eq('id', postId)
    .eq('user_id', user.data.user.id); // Additional safety check

  if (deleteError) {
    console.error('Error deleting post:', deleteError);
    throw deleteError;
  }
}

export async function toggleLike(postId: string) {
  const user = await supabase.auth.getUser();
  if (!user.data.user) throw new Error('Not authenticated');

  // Check if like exists
  const { data: existingLike } = await supabase
    .from('post_likes')
    .select()
    .eq('post_id', postId)
    .eq('user_id', user.data.user.id)
    .single();

  try {
    if (existingLike) {
      // Unlike
      await supabase
        .from('post_likes')
        .delete()
        .eq('post_id', postId)
        .eq('user_id', user.data.user.id);
      return false; // Unliked
    } else {
      // Like
      await supabase
        .from('post_likes')
        .insert([{ post_id: postId, user_id: user.data.user.id }]);
      return true; // Liked
    }
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
}

export async function getPosts() {
  const { data: posts, error } = await supabase
    .from('posts')
    .select(`
      *,
      profiles (
        username,
        avatar_url,
        artist_types
      ),
      post_likes (
        user_id
      )
    `)
    .order('created_at', { ascending: false })
    .limit(50); // Limit to prevent performance issues

  if (error) {
    throw error;
  }

  if (!posts) {
    throw new Error('No posts found');
  }
  return posts;
}

export function subscribeToPosts(callback: (post: Post, eventType: 'INSERT' | 'DELETE') => void): RealtimeChannel {
  return supabase.channel('posts')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'posts'
      },
      async (payload) => {
        // Fetch complete post data including profile
        const { data: post } = await supabase
          .from('posts')
          .select(`
            *,
            profiles (
              username,
              avatar_url,
              artist_types
            ),
            post_likes (
              user_id
            )
          `)
          .eq('id', payload.new.id)
          .single();
        
        if (post) {
          callback(post as Post, 'INSERT');
        }
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'posts'
      },
      (payload) => {
        callback(payload.old as Post, 'DELETE');
      }
    )
    .subscribe();
}